import {
  ArchiveIcon,
  CaretDownIcon,
  EyeIcon,
  InfoIcon,
  PlusIcon,
  UsersThreeIcon,
  XIcon,
} from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { EditEventContent } from '@/content/EditEventContent';
import type { UserRole } from '@/lib/types/database';
import type { InvitationGroupsSelectorProps } from '@/lib/types/events.types';
import { cn } from '@/lib/utils';
import {
  getEventAudienceRoleOrder,
  getEventAudienceGroupsForRole,
  getEventAudienceNarrowedLabel,
  getEventAudienceRoleLabel,
  getEventAudienceSectionMode,
} from '@/lib/utils/event-audience.utils';
import { eventFormClasses } from '@/lib/utils/events.utils';
import { getGroupRoleTone } from '@/lib/utils/groups.utils';

function getIconTextClassName(dotClassName: string): string {
  return dotClassName.replace(/^bg-/, 'text-');
}

function RoleDot({ role }: { role: UserRole }) {
  return (
    <span
      className={cn(
        'h-2 w-2 shrink-0 rounded-full',
        getGroupRoleTone(role).radioDotClassName
      )}
    />
  );
}

export function InvitationGroupsSelector({
  selectedRoles,
  selectedGroupIds,
  groups,
  isLoadingGroups,
  audienceMemberCount,
  isAudienceLoading,
  hasTargets,
  isPreviewDisabled,
  drillRole,
  drillRoleMemberCount,
  onDrillRoleChange,
  onPreview,
  onRoleAdd,
  onRoleSetAll,
  onGroupToggle,
  onRoleRemove,
}: InvitationGroupsSelectorProps) {
  const roleOrder = getEventAudienceRoleOrder();
  const selectedRoleEntries = roleOrder
    .map((role) => ({
      role,
      mode: getEventAudienceSectionMode(
        role,
        selectedRoles,
        selectedGroupIds,
        groups
      ),
    }))
    .filter((entry) => entry.mode !== 'none');
  const remainingRoles = roleOrder.filter(
    (role) => !selectedRoleEntries.some((entry) => entry.role === role)
  );
  const activeDrillRole = drillRole;
  const activeDrillMode = activeDrillRole
    ? getEventAudienceSectionMode(
        activeDrillRole,
        selectedRoles,
        selectedGroupIds,
        groups
      )
    : 'none';
  const activeDrillRoleLabel = activeDrillRole
    ? getEventAudienceRoleLabel(activeDrillRole)
    : '';
  const activeDrillRoleTone = activeDrillRole
    ? getGroupRoleTone(activeDrillRole)
    : null;
  const activeDrillRoleGroups = activeDrillRole
    ? getEventAudienceGroupsForRole(activeDrillRole, groups, selectedGroupIds)
    : [];

  return (
    <div className="space-y-4">
      <div>
        <label className={eventFormClasses.label}>
          {EditEventContent.invitationGroupsLabel}
        </label>
        <p className="text-small text-slate-300">
          Tap a role to invite everyone in it. Tap a chip to narrow it to
          specific groups.
        </p>
      </div>

      <div className="rounded-md border border-slate-550 bg-slate-600 p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-green-400/15 text-green-300">
              <UsersThreeIcon size={18} />
            </div>
            <div className="min-w-0">
              <p className="text-large text-slate-50">
                {isAudienceLoading
                  ? 'Calculating audience'
                  : `${audienceMemberCount} members`}
              </p>
              <p className="truncate text-small text-slate-400">
                {hasTargets
                  ? 'Deduplicated across selected targets'
                  : 'No audience selected yet'}
              </p>
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="shrink-0"
            disabled={isPreviewDisabled}
            onClick={onPreview}
          >
            <EyeIcon size={16} />
            Preview
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-slate-550 bg-slate-600 p-4">
        {selectedRoleEntries.length > 0 ? (
          <>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
              Inviting
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedRoleEntries.map(({ role, mode }) => {
                const roleLabel = getEventAudienceRoleLabel(role);
                const roleTone = getGroupRoleTone(role);
                const narrowedLabel =
                  mode === 'groups'
                    ? getEventAudienceNarrowedLabel(
                        role,
                        groups,
                        selectedGroupIds
                      )
                    : null;

                return (
                  <span
                    key={role}
                    className={cn(
                      'inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-1.5 text-sm',
                      roleTone.pillClassName
                    )}
                  >
                    <RoleDot role={role} />
                    <span className="min-w-0 leading-tight">
                      <span className="block truncate text-small font-medium">
                        {roleLabel}
                      </span>
                      {narrowedLabel && (
                        <span className="block truncate text-[11px] opacity-70">
                          {narrowedLabel}
                        </span>
                      )}
                    </span>
                    <span className="ml-1 inline-flex items-center border-l border-current/30 pl-1">
                      <button
                        type="button"
                        className="grid h-8 w-8 place-items-center rounded-full opacity-90 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
                        aria-label={`Choose groups for ${roleLabel}`}
                        onClick={() => onDrillRoleChange(role)}
                      >
                        <CaretDownIcon size={16} />
                      </button>
                      <button
                        type="button"
                        className="grid h-8 w-8 place-items-center rounded-full opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
                        aria-label={`Remove ${roleLabel}`}
                        onClick={() => onRoleRemove(role)}
                      >
                        <XIcon size={15} />
                      </button>
                    </span>
                  </span>
                );
              })}
            </div>
            <div className="my-4 h-px bg-slate-550" />
          </>
        ) : (
          <div className="mb-4 flex items-start gap-3">
            <UsersThreeIcon
              size={18}
              className="mt-0.5 shrink-0 text-slate-400"
            />
            <p className="text-body text-slate-300">
              No audience yet. Add the roles who should see this event -
              everyone in that role is included unless you narrow it.
            </p>
          </div>
        )}

        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
          {selectedRoleEntries.length > 0 ? 'Add another role' : 'Roles'}
        </p>
        <div className="flex flex-wrap gap-2">
          {remainingRoles.map((role) => {
            const roleLabel = getEventAudienceRoleLabel(role);
            const roleTone = getGroupRoleTone(role);

            return (
              <button
                key={role}
                type="button"
                className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-slate-500 px-3 py-1.5 text-small text-slate-300 transition-colors hover:border-slate-300 hover:text-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400"
                aria-label={`Add ${roleLabel}`}
                onClick={() => onRoleAdd(role)}
              >
                <PlusIcon
                  size={14}
                  className={getIconTextClassName(roleTone.radioDotClassName)}
                />
                {roleLabel}
              </button>
            );
          })}
        </div>
      </div>

      {selectedRoleEntries.length > 0 && (
        <div className="flex items-center gap-2 text-small text-slate-400">
          <InfoIcon size={13} className="shrink-0" />
          <span>
            Tap a chip's caret to choose specific groups within that role.
          </span>
        </div>
      )}

      <Drawer
        open={activeDrillRole !== null}
        onOpenChange={(open) => {
          if (!open) {
            onDrillRoleChange(null);
          }
        }}
      >
        <DrawerContent className="max-h-[85vh] bg-slate-600 px-4 pb-6">
          {activeDrillRole && activeDrillRoleTone && (
            <div className="min-h-0 overflow-y-auto pb-4">
              <DrawerHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <span
                    className={cn(
                      'h-2.5 w-2.5 shrink-0 rounded-full',
                      activeDrillRoleTone.radioDotClassName
                    )}
                  />
                  <DrawerTitle className="text-[22px]">
                    {activeDrillRoleLabel}
                  </DrawerTitle>
                </div>
                <p className="text-body text-slate-300">
                  Invite everyone, or narrow to specific groups.
                </p>
              </DrawerHeader>

              <button
                type="button"
                className={cn(
                  'mb-3 flex w-full items-center gap-3 rounded-md border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400',
                  activeDrillMode === 'all'
                    ? activeDrillRoleTone.radioClassName
                    : 'border-slate-550 bg-slate-600 text-slate-50 hover:bg-slate-550'
                )}
                role="radio"
                aria-checked={activeDrillMode === 'all'}
                onClick={() => onRoleSetAll(activeDrillRole)}
              >
                <span
                  className={cn(
                    'grid h-5 w-5 shrink-0 place-items-center rounded-full border-2',
                    activeDrillRoleTone.sectionBorderClassName
                  )}
                >
                  {activeDrillMode === 'all' && (
                    <span
                      className={cn(
                        'h-2.5 w-2.5 rounded-full',
                        activeDrillRoleTone.radioDotClassName
                      )}
                    />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-body font-medium">
                    Everyone in {activeDrillRoleLabel}
                  </span>
                  <span className="block text-small text-slate-300">
                    {drillRoleMemberCount.isLoading
                      ? `Calculating current ${activeDrillRoleLabel}`
                      : drillRoleMemberCount.memberCount !== null
                        ? `All ${drillRoleMemberCount.memberCount} current ${activeDrillRoleLabel}`
                        : `All current ${activeDrillRoleLabel}`}
                  </span>
                </span>
                {drillRoleMemberCount.memberCount !== null && (
                  <span className="shrink-0 text-small">
                    {drillRoleMemberCount.memberCount}
                  </span>
                )}
              </button>

              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Or pick groups
              </p>
              <div className="overflow-hidden rounded-md border border-slate-550">
                {isLoadingGroups ? (
                  <p className="bg-slate-600 p-3 text-small text-slate-300">
                    Loading groups...
                  </p>
                ) : activeDrillRoleGroups.length === 0 ? (
                  <p className="bg-slate-600 p-3 text-small text-slate-300">
                    No groups exist for this role.
                  </p>
                ) : (
                  activeDrillRoleGroups.map((group, index) => {
                    const checked = selectedGroupIds.has(group.id);

                    return (
                      <label
                        key={group.id}
                        className={cn(
                          'flex items-center gap-3 bg-slate-600 p-3 transition-colors',
                          index > 0 && 'border-t border-slate-550',
                          checked
                            ? activeDrillRoleTone.selectedRowClassName
                            : 'hover:bg-slate-550'
                        )}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() =>
                            onGroupToggle(activeDrillRole, group.id)
                          }
                          className={cn(
                            'h-5 w-5 rounded-[5px] border-2 border-slate-550',
                            activeDrillRoleTone.checkboxClassName
                          )}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-body text-slate-50">
                            {group.display_name}
                            {group.archived_at && (
                              <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-yellow-300/15 px-1.5 py-0.5 align-middle text-[10px] uppercase leading-none text-yellow-300">
                                <ArchiveIcon size={10} />
                                Archived
                              </span>
                            )}
                          </span>
                        </span>
                        <span className="shrink-0 text-small text-slate-400">
                          {group.member_count}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>

              <div className="mt-4 flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 flex-1 border-slate-550 bg-transparent text-slate-50 hover:bg-slate-550 hover:text-slate-50"
                  onClick={() => {
                    onRoleRemove(activeDrillRole);
                    onDrillRoleChange(null);
                  }}
                >
                  Remove role
                </Button>
                <Button
                  type="button"
                  className="h-12 flex-1"
                  onClick={() => onDrillRoleChange(null)}
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </DrawerContent>
      </Drawer>
    </div>
  );
}
